import {
  Home, Wrench, Layers, Ruler, ShieldCheck, Grid3x3, Thermometer, ArrowUpRight,
} from "lucide-react";
import { SERVICES, IMAGES } from "../../data/content";
import { scrollToId } from "../../hooks/useSmoothScroll";
import { Reveal, Overline } from "./Reveal";

const ICONS = { Home, Wrench, Layers, Ruler, ShieldCheck, Grid3x3, Thermometer };
const SPAN = {
  tetofedes: "lg:col-span-6",
  tetojavitas: "lg:col-span-6",
  lemezteto: "lg:col-span-4",
  badogos: "lg:col-span-4",
  palateto: "lg:col-span-4",
  cserepeslemez: "lg:col-span-6",
  tetoszigeteles: "lg:col-span-6",
};
const FEATURED = { tetofedes: IMAGES.hero, tetoszigeteles: IMAGES.serviceHighlight };

function ServiceCard({ s, index }) {
  const Icon = ICONS[s.icon];
  const bg = FEATURED[s.id];

  if (bg) {
    return (
      <div
        data-testid={`service-card-${s.id}`}
        className="group relative h-full w-full overflow-hidden rounded-2xl min-h-[320px] flex flex-col justify-end p-7"
      >
        <img src={bg} alt={s.title} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0E5A75] via-[#0E5A75]/70 to-[#0E5A75]/10" />
        <div className="relative">
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#0E5A75] text-white mb-4">
            <Icon className="h-6 w-6" />
          </span>
          <h3 className="text-2xl font-bold text-white">{s.title}</h3>
          <p className="mt-2 text-sm text-slate-200 leading-relaxed max-w-md">{s.desc}</p>
          <button
            data-testid={`service-cta-${s.id}`}
            onClick={() => scrollToId("ajanlatkeres")}
            className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-white/95 px-5 py-2.5 text-sm font-semibold text-[#0E5A75] hover:bg-[#0E5A75] hover:text-white transition-colors"
          >
            Ajánlatot kérek <ArrowUpRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid={`service-card-${s.id}`}
      className="group flex h-full w-full flex-col rounded-2xl border border-slate-200 bg-white p-7 hover:border-emerald-200 hover:shadow-xl hover:shadow-slate-200/60 hover:-translate-y-1 transition-all duration-300"
    >
      <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 group-hover:bg-[#0E5A75] group-hover:text-white transition-colors">
        <Icon className="h-6 w-6" />
      </span>
      <h3 className="mt-5 text-xl font-bold text-[#0E5A75]">{s.title}</h3>
      <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1">{s.desc}</p>
      <button
        data-testid={`service-cta-${s.id}`}
        onClick={() => scrollToId("ajanlatkeres")}
        className="mt-5 inline-flex items-center gap-1.5 self-start rounded-full border border-slate-300 px-5 py-2.5 text-sm font-semibold text-[#0E5A75] hover:border-emerald-500 hover:bg-[#0E5A75] hover:text-white transition-colors"
      >
        Ajánlatot kérek <ArrowUpRight className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function Services() {
  return (
    <section id="szolgaltatasok" data-testid="services-section" className="scroll-mt-24 bg-slate-50 py-16 sm:py-20 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <Reveal><Overline>Szolgáltatásaink</Overline></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 text-4xl sm:text-5xl font-bold tracking-tight text-gradient-brand text-balance">
                Teljes körű tetőmegoldások egy kézből
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <p className="max-w-sm text-base text-slate-600 leading-relaxed">
              A tetőfedéstől a bádogos munkákon át a szigetelésig — minden szolgáltatáshoz
              helyszíni felmérést és szaktanácsadást biztosítunk.
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5">
          {SERVICES.map((s, i) => (
            <Reveal key={s.id} delay={(i % 3) * 0.06} className={SPAN[s.id]}>
              <ServiceCard s={s} index={i} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
